import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import {
  NumberParam,
  StringParam,
  useQueryParam,
  withDefault
} from 'use-query-params';
import {
  Button,
  LinearProgress,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel
} from '@material-ui/core';
import { ArrowDropDown as ArrowDropDownIcon } from '@material-ui/icons';
import { Alert } from '@material-ui/lab';
import { WorkflowStatus as WorkflowV2Status } from '@sierralabs/workflow-core';
import { CreateWorkflowDialog } from '@sierralabs/workflow-ui';

import PageTemplate from '../../common/page-template/PageTemplate';
import PageTemplateCardOver from '../../common/page-template/PageTemplateCardOver';
import PageTemplateHeader, {
  PageTemplateHeaderActions
} from '../../common/page-template/PageTemplateHeader';
import { formatDate } from '../../common/formatter';
import SearchBox from '../../common/SearchBox';
import StatusLabel from '../../common/StatusLabel';
import UserLink from '../users/components/UserLink';
import WorkflowApiLevelLabel from './components/WorkflowApiLevelLabel';
import WorkflowCreateDialog from './dialogs/WorkflowCreateDialog';
import { useFindWorkflowList } from './Workflow.hooks';
import { WorkflowListItem, WorkflowStatus } from './Workflow.interface';

const StyledWorkflowApiLevelLabel = styled(WorkflowApiLevelLabel)`
  margin-left: 8px;
`;

const columns: { key: string; label: string }[] = [
  { key: 'name', label: 'Workflow Name' },
  { key: 'taskType', label: 'Task Type' },
  { key: 'version', label: 'Version' },
  { key: 'apiLevel', label: 'API Level' },
  { key: 'status', label: 'Status' },
  { key: 'modified', label: 'Last Modified' },
  { key: 'modifiedBy', label: 'Modified By' }
];

const WorkflowListPage: React.FC = () => {
  const history = useHistory();
  const [search, setSearch] = useQueryParam(
    'search',
    withDefault(StringParam, '')
  );
  const [page, setPage] = useQueryParam('page', withDefault(NumberParam, 0));
  const [limit, setLimit] = useQueryParam(
    'limit',
    withDefault(NumberParam, 100)
  );
  const [order, setOrder] = useQueryParam(
    'order',
    withDefault(StringParam, '')
  );
  const [direction, setDirection] = useQueryParam(
    'direction',
    withDefault(StringParam, 'ASC')
  );
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isOpenCreateDialog, setIsOpenCreateDialog] = useState(false);
  const [isOpenCreateDialogV2, setIsOpenCreateDialogV2] = useState(false);
  const { workflowList, isLoading, loadingError, findWorkflows } =
    useFindWorkflowList();

  const handleSort = (column: string) => {
    setOrder(column);
    setDirection(direction === 'ASC' ? 'DESC' : 'ASC');
  };

  const getSortDirection = (column: string): 'asc' | 'desc' => {
    return order === column && direction === 'DESC' ? 'desc' : 'asc';
  };

  const goToDetailPage = (
    id: string | number,
    apiLevel: WorkflowListItem['apiLevel']
  ) => {
    const path =
      apiLevel === 1
        ? `/portal/admin/workflow/${id}`
        : `/portal/admin/v2/workflows/${id}`;
    history.push(path);
  };

  const findWorkflowsWithParams = useCallback(() => {
    findWorkflows({ page, limit, order, direction, search });
  }, [page, limit, order, direction, search, findWorkflows]);

  useEffect(() => {
    findWorkflowsWithParams();
  }, [findWorkflowsWithParams]);

  return (
    <PageTemplate>
      <PageTemplateHeader>
        <h2>Workflow</h2>
        <SearchBox value={search} onSearch={value => setSearch(value)} />
        <PageTemplateHeaderActions>
          <Button
            color='primary'
            variant='contained'
            endIcon={<ArrowDropDownIcon />}
            onClick={event => setAnchorEl(event.currentTarget)}
          >
            Add Workflow
          </Button>
          <Menu
            id='workflow-list-menu'
            open={!!anchorEl}
            anchorEl={anchorEl}
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            onClose={() => setAnchorEl(null)}
          >
            <MenuItem disabled>Choose Workflow API</MenuItem>
            <MenuItem
              onClick={() => {
                setIsOpenCreateDialog(true);
                setAnchorEl(null);
              }}
            >
              Create Workflow <StyledWorkflowApiLevelLabel apiLevel={1} />
            </MenuItem>
            <MenuItem
              onClick={() => {
                setIsOpenCreateDialogV2(true);
                setAnchorEl(null);
              }}
            >
              Create Workflow <StyledWorkflowApiLevelLabel apiLevel={2} />
            </MenuItem>
          </Menu>
        </PageTemplateHeaderActions>
      </PageTemplateHeader>
      <PageTemplateCardOver>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {columns.map(column => (
                  <TableCell key={column.key}>
                    <TableSortLabel
                      active={order === column.key}
                      direction={getSortDirection(column.key)}
                      onClick={() => handleSort(column.key)}
                    >
                      {column.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell padding='none' colSpan={columns.length}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              )}
              {workflowList?.data.map(workflow => (
                <TableRow
                  hover
                  key={`${workflow.id}-${workflow.apiLevel}`}
                  onClick={() => goToDetailPage(workflow.id, workflow.apiLevel)}
                >
                  <TableCell>{workflow.name}</TableCell>
                  <TableCell>{workflow.taskType}</TableCell>
                  <TableCell>
                    {/* use `version - 1` for published workflows */}
                    {workflow.version -
                      +(
                        workflow.status ===
                        (workflow.apiLevel === 1
                          ? WorkflowStatus.Published
                          : WorkflowV2Status.Published)
                      )}
                  </TableCell>
                  <TableCell>
                    <WorkflowApiLevelLabel apiLevel={workflow.apiLevel} />
                  </TableCell>
                  <TableCell>
                    <StatusLabel status={workflow.status} />
                  </TableCell>
                  <TableCell>{formatDate(workflow.modified)}</TableCell>
                  <TableCell>
                    <UserLink
                      type='name'
                      disableLink={true}
                      user={workflow.modifiedBy}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component='div'
          count={workflowList?.total || 0}
          rowsPerPage={workflowList?.limit || 100}
          page={workflowList?.page ? workflowList?.page - 1 : 0}
          onPageChange={(_, page) => setPage(page)}
          onRowsPerPageChange={event => {
            setLimit(+event.target.value);
            setPage(0);
          }}
        />
      </PageTemplateCardOver>
      {loadingError && (
        <Alert severity='error'>{`Error occurred. ${loadingError.message}`}</Alert>
      )}
      {isOpenCreateDialog && (
        <WorkflowCreateDialog
          open={isOpenCreateDialog}
          onCancel={() => setIsOpenCreateDialog(false)}
          onCreated={workflow => {
            setIsOpenCreateDialog(false);
            goToDetailPage(workflow.id, 1);
          }}
        />
      )}
      {isOpenCreateDialogV2 && (
        <CreateWorkflowDialog
          open
          onCancel={() => setIsOpenCreateDialogV2(false)}
          onCreated={workflow => {
            setIsOpenCreateDialogV2(false);
            goToDetailPage(workflow.id, 2);
          }}
        />
      )}
    </PageTemplate>
  );
};

export default WorkflowListPage;
